import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Grid, SxProps, Typography} from '@mui/material';

import {DataComparisonBar} from '../DataComparisonBar/DataComparisonBar';
import {EmpionPaper} from '../EmpionPaper/EmpionPaper';

import {LanguageContext} from '../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../layouts/theme/designSystem';
import {MethodItem} from '../../types/shared-types';

interface ComparisonCardProps {
  methodItem: MethodItem;
  overlap?: number;
  firstIndicatorValue: number;
  secondIndicatorValue: number;
  paperBackgroundColor?: string;
  barColor?: string;
  indicatorSize?: string;
  bubbleSize?: number;
  titleStyles?: SxProps;
  questionStyles?: SxProps;
  overlapStyles?: SxProps;
  overridePaperStyles?: SxProps;
  questionsMargin?: string;
  cardMarginBottom?: string;
  alignTop?: boolean;
}

export const ComparisonCard = ({
  methodItem,
  barColor,
  firstIndicatorValue,
  secondIndicatorValue,
  indicatorSize,
  overlap,
  bubbleSize,
  titleStyles,
  questionStyles,
  overridePaperStyles,
  questionsMargin,
  overlapStyles,
  cardMarginBottom,
}: ComparisonCardProps) => {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext);

  const paperStyles: SxProps = {
    backgroundColor: orangeColors[5],
    width: '100%',
    boxShadow: 'none',
    padding: {xs: 2, sm: 4},
    borderRadius: '16px',
    border: `1px solid ${orangeColors[3]}`,
    ...overridePaperStyles,
  };

  return (
    <Grid item width='100%' mb={cardMarginBottom || 3}>
      {/* Overlap Container */}
      <Grid item sx={{marginBottom: 1}}>
        {overlap && (
          <Typography
            variant='body1'
            sx={{color: greenColors[700], ...overlapStyles}}
            textAlign={'center'}
            fontWeight={'700'}
          >
            {t('companyCultureDashboard.employeeSatisfaction.modal.overlap', {
              overlap,
            })}
            %
          </Typography>
        )}
      </Grid>

      {/* Paper container */}
      <Grid item container>
        <EmpionPaper sx={paperStyles}>
          <Typography variant='body1' color={greenColors[700]} textAlign={'center'} sx={titleStyles}>
            {methodItem.title[currentLanguage]}
          </Typography>
          <DataComparisonBar
            indicators={[
              {value: firstIndicatorValue, color: orangeColors[100], height: indicatorSize},
              {value: secondIndicatorValue, color: greenColors[100], height: indicatorSize},
            ]}
            bubbleAmount={6}
            bubbleSize={bubbleSize}
            barColor={barColor}
          />
          <Grid item container justifyContent={'space-between'} mt={questionsMargin || 6}>
            <Grid item xs={12} sm={6} mb={{xs: 2, md: 0}}>
              <Typography variant='body1' color={greenColors[700]} textAlign={'left'} sx={questionStyles}>
                {methodItem.scaleConfiguration.labelLeft[currentLanguage]}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant='body1' color={greenColors[700]} textAlign={'right'} sx={questionStyles}>
                {methodItem.scaleConfiguration.labelRight[currentLanguage]}
              </Typography>
            </Grid>
          </Grid>
        </EmpionPaper>
      </Grid>
    </Grid>
  );
};
